import { useCallback, useEffect, useMemo, useState } from 'react';
import { applyPricing, getProductsPricing } from '../api/products';
import type { Product, ProductPricing } from '../types';

/** Where a product's price stands: fetched live, still on its way, or not to be had. */
export type PricingState = 'live' | 'loading' | 'unavailable';

type PricingMap = Map<number, ProductPricing>;

// The whole-catalogue answer is shared by every try-on surface, which often open within seconds
// of each other. One request serves them for a short while; after that the next asker fetches
// fresh numbers, so prices stay live while the frame details stay cached.
const ALL_TTL_MS = 30_000;
let allPricing: { at: number; promise: Promise<PricingMap | null> } | null = null;

function loadAllPricing(): Promise<PricingMap | null> {
  if (allPricing && Date.now() - allPricing.at < ALL_TTL_MS) return allPricing.promise;
  const entry = { at: Date.now(), promise: getProductsPricing() };
  allPricing = entry;
  // A failure isn't worth remembering; let the next asker try again.
  entry.promise.then((m) => { if (!m && allPricing === entry) allPricing = null; });
  return entry.promise;
}

/**
 * Lay live price and stock over products whose details were cached without them
 * (stripCommerce). Until the live numbers land the state is 'loading' and nothing may show a
 * price or be bought. `all` asks for the whole catalogue in one call; otherwise just the ids
 * given. A product the live answer leaves out is no longer on sale, so it comes back out of
 * stock. `withLive` does the same for a single product that isn't in the list.
 */
export function useLivePricing(products: Product[], opts: { all?: boolean } = {}) {
  const [pricing, setPricing] = useState<PricingMap | null>(null);
  const [failed, setFailed] = useState(false);

  const idsKey = opts.all
    ? 'all'
    : Array.from(new Set(products.map((p) => p.id))).sort((a, b) => a - b).join(',');

  useEffect(() => {
    if (!idsKey) return;
    let cancelled = false;
    const load = idsKey === 'all' ? loadAllPricing() : getProductsPricing(idsKey.split(',').map(Number));
    load.then((map) => {
      if (cancelled) return;
      if (map) setPricing(map);
      else setFailed(true);
    });
    return () => { cancelled = true; };
  }, [idsKey]);

  const withLive = useCallback(
    (p: Product): Product => {
      if (!pricing) return p;
      const live = pricing.get(p.id);
      return live ? applyPricing(p, live) : { ...p, quantity: 0 };
    },
    [pricing]
  );

  const priced = useMemo(() => (pricing ? products.map(withLive) : products), [products, pricing, withLive]);

  const state: PricingState = pricing ? 'live' : failed ? 'unavailable' : 'loading';
  return { products: priced, state, withLive };
}
